<template>
    <div class="flex flex-col items-center max-w-4xl mx-auto">
        <p
            v-if="linksLimitText"
            class="w-full mt-4 text-sm text-theme-muted text-center"
        >
            {{ linksLimitText }}
        </p>

        <div v-if="links.length" class="w-full mt-6 space-y-2">
            <div
                v-for="link in links"
                :key="link.id"
                class="bg-theme-card border border-theme-border rounded-xl p-4 flex items-center justify-between"
            >
                <div class="flex items-center gap-3 flex-1 min-w-0">
                    <img
                        v-if="link.image"
                        :src="link.image.url"
                        :alt="link.title"
                        class="w-10 h-10 rounded-lg object-cover border border-theme-border flex-shrink-0"
                    />
                    <div class="flex-1 min-w-0">
                        <h4 class="font-medium text-theme-base truncate">
                            {{ link.title }}
                        </h4>
                        <p class="text-sm text-theme-muted truncate">
                            {{ link.url }}
                        </p>
                    </div>
                </div>
                <div class="flex items-center gap-2 ml-3">
                    <button
                        class="text-sm text-theme-muted hover:text-stack-primary"
                        @click="startEdit(link)"
                    >
                        Editar
                    </button>
                    <button
                        class="text-sm text-theme-muted hover:text-theme-danger"
                        @click="removeLink(link.id)"
                    >
                        Eliminar
                    </button>
                </div>
            </div>
        </div>

        <div
            v-if="links.length < maxLinks"
            class="w-full mt-6 bg-theme-card border-2 border-dashed border-theme-border rounded-xl p-5"
        >
            <div class="space-y-3">
                <div v-if="!editingId">
                    <p class="text-sm text-theme-muted mb-2">
                        Ideas para empezar:
                    </p>
                    <div class="flex flex-wrap gap-2">
                        <button
                            v-for="suggestion in suggestions"
                            :key="suggestion.key"
                            type="button"
                            class="rounded-full border px-3 py-1 text-sm transition"
                            :class="
                                selectedSuggestionKey === suggestion.key
                                    ? 'border-theme-primary text-theme-primary'
                                    : 'border-theme-border text-theme-base hover:bg-theme-hover'
                            "
                            @click="applySuggestion(suggestion.key)"
                        >
                            {{ suggestion.icon }} {{ suggestion.title }}
                        </button>
                    </div>
                </div>

                <custom-checkbox v-model="includeImage" primary>
                    Incluir imagen
                </custom-checkbox>

                <div
                    v-if="includeImage"
                    class="flex flex-col items-center gap-2"
                >
                    <template v-if="form.image">
                        <img
                            :src="form.image.url"
                            alt="Imagen del enlace"
                            class="w-24 h-24 rounded-xl object-cover border border-theme-border"
                        />
                        <button
                            class="text-xs text-theme-muted hover:text-theme-danger"
                            @click="form.image = null"
                        >
                            Quitar imagen
                        </button>
                    </template>
                    <url-image-manager
                        v-else
                        always-upload
                        default-alt="Imagen del enlace"
                        :allow-edit-alt="false"
                        default-size="customImage"
                        @updated="onImageUpdated"
                    />
                </div>

                <input
                    v-model="form.title"
                    placeholder="Texto del botón (Ej: Cómo llegar) *"
                    maxlength="120"
                    class="outline-none text-base w-full border border-theme-border rounded-lg px-3 py-2 bg-theme-input text-theme-base"
                    :class="{ 'border-theme-danger': errors.title }"
                />
                <p v-if="errors.title" class="text-theme-danger text-xs">
                    {{ errors.title }}
                </p>

                <input
                    v-model="form.url"
                    :placeholder="urlPlaceholder"
                    class="outline-none text-base w-full border border-theme-border rounded-lg px-3 py-2 bg-theme-input text-theme-base"
                    :class="{ 'border-theme-danger': errors.url }"
                />
                <p v-if="errors.url" class="text-theme-danger text-xs">
                    {{ errors.url }}
                </p>
                <p v-else class="text-theme-muted text-xs">
                    {{ urlHint }}
                </p>

                <div class="flex gap-3">
                    <button
                        v-if="editingId"
                        class="flex-1 py-2 rounded-lg font-medium text-sm border border-theme-border text-theme-base hover:bg-theme-hover transition"
                        @click="cancelEdit"
                    >
                        Cancelar
                    </button>
                    <button
                        :disabled="isSaving"
                        class="flex-1 py-2 rounded-lg font-medium text-sm bg-theme-hover text-theme-base hover:opacity-80 transition"
                        @click="saveLink"
                    >
                        {{ editingId ? "Actualizar" : "+ Agregar enlace" }}
                    </button>
                </div>
            </div>
        </div>

        <p
            v-if="links.length >= maxLinks"
            class="w-full mt-6 text-sm text-theme-muted text-center"
        >
            Ya tienes los {{ maxLinks }} enlaces que incluye tu plan.
        </p>

        <p v-if="error" class="text-theme-danger text-sm mt-4">
            {{ error }}
        </p>
    </div>
</template>

<script>
import onboardingCompletedGuard from "@/mixins/onboardingCompletedGuard";
import onboardingStepNavigation from "@/mixins/onboardingStepNavigation";
import planLimitError from "@/mixins/planLimitError";
import { mapActions, mapMutations, mapState } from "vuex";

import {
    LINK_SUGGESTIONS,
    linkSuggestionKeys
} from "@/common/fields/link-suggestions";
import { SAMPLE_WHATSAPP_NUMBER } from "@/common/lib/onboarding-preview-samples";
import { ensureUrlProtocol } from "@/common/lib/url";
import { buildWhatsappWaMeLink } from "@/common/lib/whatsapp";

import CustomCheckbox from "@/components/utils/CustomCheckbox.vue";
import UrlImageManager from "@/components/utils/UrlImageManager.vue";

const MAX_LINKS = 20;
// Mismo valor que el seed de planes (Starter: `customLinks: 20`).
const STARTER_LINKS_LIMIT = 20;

export default {
    name: "OnboardingCustomLinksPage",

    components: { CustomCheckbox, UrlImageManager },

    mixins: [
        onboardingCompletedGuard,
        onboardingStepNavigation,
        planLimitError
    ],

    data() {
        return {
            stepKey: "custom-links",
            links: [],
            form: { title: "", url: "", image: null },
            includeImage: false,
            errors: {},
            editingId: null,
            selectedSuggestionKey: null,
            isSaving: false,
            isLoading: false,
            error: null,
            maxLinks: MAX_LINKS,
            planName: "",
            // Tope `customLinks` del plan vigente; null = sin límite.
            linksLimit: null
        };
    },

    computed: {
        ...mapState("onboarding", ["businessType"]),
        suggestions() {
            return linkSuggestionKeys(
                this.selections.businessType || this.businessType
            ).map((key) => ({ key, ...LINK_SUGGESTIONS[key] }));
        },
        selectedSuggestion() {
            return LINK_SUGGESTIONS[this.selectedSuggestionKey] || null;
        },
        urlPlaceholder() {
            return this.selectedSuggestion
                ? this.selectedSuggestion.urlPlaceholder
                : "Ej: https://maps.app.goo.gl/...";
        },
        urlHint() {
            return this.selectedSuggestion
                ? this.selectedSuggestion.hint
                : "Abre la página que quieres compartir, copia la dirección (o usa el botón Compartir de la app) y pégala aquí.";
        },
        linksLimitText() {
            if (this.linksLimit === null || !this.planName) return "";
            const base = `Tu plan ${this.planName} incluye hasta ${this.linksLimit} enlaces.`;
            return this.planName === "Free"
                ? `${base} Con Starter llegas a ${STARTER_LINKS_LIMIT}.`
                : base;
        },
        localStepperUi() {
            return {
                nextDisabled: false,
                loading: this.isLoading,
                nextLabel: "Continuar",
                nextVariant: "primary"
            };
        }
    },

    async mounted() {
        await this.ensureOnboardingStatus();
        if (this.redirectIfOnboardingCompleted()) return;
        try {
            const data = await this.listCustomLinks();
            this.links = Array.isArray(data) ? data : data.items || [];
        } catch {
            // No existing links
        }
        this.SET_DRAFT_CUSTOM_LINKS(this.links);
        this.loadLinksLimit();
    },

    methods: {
        ...mapActions("customLinks", [
            "listCustomLinks",
            "createCustomLink",
            "updateCustomLink",
            "deleteCustomLink"
        ]),
        ...mapActions("onboarding", ["confirmCustomLinks"]),
        ...mapActions("platformSubscriptions", ["getMySubscription"]),

        // El tope sale del plan elegido (este paso va después de `plan`).
        // Sin dato se queda el máximo fijo y el backend valida igual.
        async loadLinksLimit() {
            try {
                const subscription = await this.getMySubscription();
                const usage = (subscription?.usage || []).find(
                    (item) => item.resourceCode === "customLinks"
                );
                if (!usage || usage.limit === null) return;
                this.linksLimit = usage.limit;
                this.maxLinks = Math.min(usage.limit, MAX_LINKS);
                this.planName = subscription?.current?.planName || "";
            } catch {
                // Sin detalle del plan: se queda el máximo fijo
            }
        },
        ...mapMutations("onboarding", ["SET_DRAFT_CUSTOM_LINKS"]),

        validateForm() {
            const errors = {};
            if (!this.form.title.trim()) {
                errors.title = "El título es obligatorio";
            }
            this.form.url = ensureUrlProtocol(this.form.url);
            if (!this.form.url) {
                errors.url = "La URL es obligatoria";
            } else {
                try {
                    new URL(this.form.url.trim());
                } catch {
                    errors.url = "Ingresa una URL válida";
                }
            }
            this.errors = errors;
            return Object.keys(errors).length === 0;
        },

        // Devuelve si quedó guardado: "Continuar" lo usa para no perder un
        // enlace escrito sin agregar.
        async saveLink() {
            if (!this.validateForm() || this.isSaving) return false;

            this.isSaving = true;
            try {
                const payload = {
                    title: this.form.title.trim(),
                    url: this.form.url.trim()
                };
                if (this.includeImage && this.form.image) {
                    payload.imageId = this.form.image.id;
                } else if (this.editingId) {
                    payload.imageId = null;
                }
                if (this.editingId) {
                    await this.updateCustomLink({
                        id: this.editingId,
                        ...payload
                    });
                } else {
                    await this.createCustomLink(payload);
                }
                const data = await this.listCustomLinks();
                this.links = Array.isArray(data) ? data : data.items || [];
                this.SET_DRAFT_CUSTOM_LINKS(this.links);
                this.cancelEdit();
                this.isSaving = false;
                return true;
            } catch (err) {
                this.isSaving = false;
                if (!this.openPlanUpgradeForLimit(err)) {
                    this.error = err.message || "Error al guardar el enlace";
                }
                return false;
            }
        },

        hasPendingLink() {
            return Boolean(this.form.title.trim() || this.form.url.trim());
        },

        onImageUpdated(url, image) {
            this.form.image = { id: image.id, url };
        },

        startEdit(link) {
            this.editingId = link.id;
            this.form = {
                title: link.title,
                url: link.url,
                image: link.image || null
            };
            this.includeImage = Boolean(link.image);
            this.errors = {};
        },

        applySuggestion(key) {
            this.selectedSuggestionKey = key;
            this.form.title = LINK_SUGGESTIONS[key].title;
            const number = this.onboardingDraft.whatsappNumber;
            if (
                key === "contact" &&
                !this.form.url &&
                number &&
                number !== SAMPLE_WHATSAPP_NUMBER
            ) {
                this.form.url = buildWhatsappWaMeLink(number);
            }
        },

        cancelEdit() {
            this.editingId = null;
            this.selectedSuggestionKey = null;
            this.form = { title: "", url: "", image: null };
            this.includeImage = false;
            this.errors = {};
        },

        async removeLink(id) {
            try {
                await this.deleteCustomLink({ id });
                this.links = this.links.filter((l) => l.id !== id);
                this.SET_DRAFT_CUSTOM_LINKS(this.links);
                if (this.editingId === id) {
                    this.cancelEdit();
                }
            } catch (err) {
                this.error = err.message || "Error al eliminar el enlace";
            }
        },

        // Enlaces es opcional, pero después viene el step plan (obligatorio).
        skipStep() {
            this.$router.push(this.routeForStep("plan"));
        },

        stepperNext() {
            if (this.readOnly) {
                this.goToNextReachable();
                return;
            }
            this.confirmStep();
        },

        async confirmStep() {
            if (this.isLoading) return;
            if (this.hasPendingLink() && !(await this.saveLink())) return;
            this.isLoading = true;
            this.error = null;
            try {
                const data = await this.confirmCustomLinks();
                const flow = data.onboardingFlow;
                if (flow && flow.nextStep) {
                    const route = this.routeForStep(flow.nextStep);
                    if (route) {
                        this.$router.push(route);
                        return;
                    }
                }
                this.$router.push(this.completionRoute);
                this.isLoading = false;
            } catch (err) {
                this.isLoading = false;
                this.error = err.message || "Error al confirmar";
            }
        }
    }
};
</script>
